import React, { useState, useRef, useEffect } from 'react';
import SimpleBar from "simplebar-react";
import { MessageSquare, Bot, User, Send } from "lucide-react";
import { searchRAG } from 'services/knowledge.service';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: string;
  sendFrom: 'User' | 'Agent';
}

const RetrievalTesting = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: 'Hello! I\'m your AI assistant. I can help you with knowledge retrieval and answer your questions. What would you like to know?',
      sendFrom: 'Agent',
      isUser: false,
      timestamp: new Date().toISOString()
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const chatRef = useRef<any>(null);

  useEffect(() => {
    if (chatRef.current?.el) {
      chatRef.current.getScrollElement().scrollTop = chatRef.current.getScrollElement().scrollHeight;
    }
  }, [messages]);

  const formatDateTime = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return '';
    }
  };


  const addMessage = () => {
    if (currentMessage.trim() !== '') {
      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        content: currentMessage,
        isUser: true,
        sendFrom: 'User',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, userMessage]);
      setCurrentMessage('');
      setIsTyping(true);

      // Simulate AI response delay
      setTimeout(async () => {
        // Add AI response
        const payload = {
          History: messages,
          LastMessage: currentMessage
        };
        const response = await searchRAG(payload);
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response,
          isUser: false,
          sendFrom: 'Agent',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, aiMessage]);
        setIsTyping(false);
      }, 1500 + Math.random() * 1000); // Random delay between 1.5-2.5 seconds
    }
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMessage();
    }
  };

  return (
    <div className="w-full h-full overflow-auto !mb-0 flex flex-col">
      {/* Header */}
      <div className=" pb-2 border-b border-slate-200 dark:border-zink-500">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center size-10 bg-custom-100 dark:bg-custom-500/20 rounded-full shrink-0">
            <Bot className="size-5 text-custom-500" />
          </div>
          <div>
            <h6 className="text-base font-semibold">Alle Assistant</h6>
            <p className="text-sm text-slate-500 dark:text-zink-300">Test the AI retrieval functionality</p>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="relative bg-slate-50 dark:bg-zink-600/50 flex-1 min-h-0">
        <SimpleBar ref={chatRef} className="h-full px-4 bg-white">
          <ul className="flex flex-col gap-6 list-none py-4">
            {messages.map((message, index) => (
              <li
                key={message.id}
                className={`relative flex chat-message group/item ${!message.isUser ? 'justify-start' : 'justify-end'}`}
              >
                <span className={`absolute text-[10px] text-gray-500 ${message.isUser
                  ? "right-[48px] top-[-13px]"
                  : "left-[48px] top-[-13px]"
                  } bottom-[2px]`}>
                  {formatDateTime(message.timestamp)}
                </span>

                <div className="flex gap-3 max-w-[80%] md:max-w-[70%]">
                  {/* Avatar */}
                  <div className={`flex items-center self-end justify-center text-sm font-semibold rounded-full size-9 shrink-0 ${message.isUser ? 'order-3' : 'order-1'
                    }`}>
                    {message.isUser ? (
                      <div className="bg-slate-200 dark:bg-zink-600 text-slate-500 dark:text-zink-200 rounded-full size-9 flex items-center justify-center">
                        <User className="size-5" />
                      </div>
                    ) : (
                      <div className="bg-custom-100 dark:bg-custom-500/20 text-custom-500 rounded-full size-9 flex items-center justify-center">
                        <Bot className="size-5" />
                      </div>
                    )}
                  </div>

                  {/* Message Bubble */}
                  <div className={`flex flex-col gap-3 ${message.isUser ? 'order-2' : 'order-2'}`}>
                    <div className="flex gap-3 relative">
                      <div className={`p-4 rounded-md shadow-sm break-words ${message.isUser
                        ? "bg-custom-500 text-white rounded-br-none"
                        : "bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-100 border border-slate-200 dark:border-zink-600 rounded-bl-none"
                        }`}>
                        {message.content}
                      </div>

                      {/* Message Type Icon */}
                      <span className={`absolute ${message.isUser ? "right-[4px]" : "left-[4px]"
                        } bottom-[2px] p-0 m-0 leading-normal`}>
                        <MessageSquare size={8} className={message.isUser ? "text-white/70" : "text-slate-400"} />
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <li className="relative flex chat-message justify-start">
                <div className="flex gap-3">
                  <div className="bg-custom-100 dark:bg-custom-500/20 text-custom-500 rounded-full size-9 flex items-center justify-center shrink-0">
                    <Bot className="size-5" />
                  </div>
                  <div className="bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-600 p-4 rounded-md rounded-bl-none">
                    <div className="flex space-x-1">
                      <div className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"></div>
                      <div className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </SimpleBar>
      </div>

      {/* Input Area */}
      <div className="card-body pt-2 border-t border-slate-200 dark:border-zink-500">
        <div className="flex items-center gap-2">
          <div className="grow min-w-0">
            <input
              type="text"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
              placeholder="Ask me anything about the knowledge base..."
              value={currentMessage}
              onKeyDown={onKeyPress}
              onChange={(e) => setCurrentMessage(e.target.value)}
              disabled={isTyping}
            />
          </div>
          <div className="shrink-0">
            <button
              type="button"
              className="btn bg-custom-500 border-custom-500 text-white hover:bg-custom-600 hover:border-custom-600 focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center size-10"
              onClick={addMessage}
              disabled={isTyping || currentMessage.trim() === ''}
            >
              <Send className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetrievalTesting;
